import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    MenuItem,
    InputLabel,
    FormControl,
    Select,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    Divider,
    useTheme,
    Menu,
    Card,
    CardContent,
    CardHeader,
    InputAdornment,
    Alert,
    Stepper,
    Step,
    StepLabel,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    FormControlLabel,
    Checkbox,
    ListItemIcon,
    TableFooter,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack
} from '@mui/material';
import {
    Search,
    Close as CloseIcon,
    Today as TodayIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    PictureAsPdf,
    TableChart,
    GetApp,
    X,
    MeetingRoom,
    Person,
    Phone,
    Email,
    CreditCard,
    CheckCircle,
    CalendarToday,
    Receipt,
    AccessTime,
    ExitToApp,
    AttachMoney,
    Warning,
    LocalHotel,
    Payment,
    Business,
    People,
    Group,
    ExpandMore,
    ReceiptLong,
    Paid,
    ClearAll,
    Print
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
    APIBookingList,
    APIBookingCheckOut,
    APIRoomNames,
    APIGuestOrders,
    APIProcessCheckout,
    APICheckoutSummary
} from '../../services/APIs';
import { TEXT_CONNECTION_ERROR, TEXT_DATA_LOADED } from '../../services/Text';
import {
    formatCreatedAtDate,
    formatNumberWithComma,
    getCategoriesNameById,
    getStatusById,
    getUserDetails,
    recordsPerPage,
    formatCurrency,
    formatNumberWithComma2
} from '../../utils/helpers';
import {
    exportToCSV,
    exportToExcel,
    exportToPDF
} from '../../services/reservation/CheckOutService';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../utils/Permission';
import useCustomToast from '../../hooks/useToast';
import PrintCheckoutReceipt from './PrintCheckoutReceipt';

const CheckOut = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [earlyCheckOutModal, setEarlyCheckOutModal] = useState(false);
    const [lateCheckOutModal, setLateCheckOutModal] = useState(false);

    // State management
    const [pageState, setPageState] = useState({
        pageData: []
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Data states
    const [rooms, setRooms] = useState([]);
    const [guestOrders, setGuestOrders] = useState({
        orders: [],
        bar_orders: [],
        totals: {
            restaurant_orders: 0,
            bar_orders: 0,
            grand_total: 0
        }
    });
    const [checkoutSummary, setCheckoutSummary] = useState(null);
    const [loadingOrders, setLoadingOrders] = useState(false);

    // Print receipt state
    const [printReceiptData, setPrintReceiptData] = useState(null);
    const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);

    // Filters state
    const [filters, setFilters] = useState({
        booking_no: '',
        customer_name: '',
        room_type: '',
        status: '2',
        dateFrom: '',
        dateTo: ''
    });

    // Check-out form state
    const [checkOutForm, setCheckOutForm] = useState({
        booking_id: '',
        user_id: getUserDetails("Id"),
        branch_id: getUserDetails("branchId"),
        comments: '',
        performed_by: getUserDetails("name") || '',
        final_amount: 0,
        balance_paid: 0,
        additional_charges: 0,
        late_checkout_charges: 0,
        damage_charges: 0,
        other_charges: 0,
        orders_total: 0,
        bar_orders_total: 0,
        grand_total: 0,
        payment_method: 'cash',
        payment_notes: '',
        include_orders: true,
        include_bar_orders: true
    });

    // Dialog states
    const [filterOptions, setFilterOptions] = useState(false);
    const [openCheckOut, setOpenCheckOut] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
    const [paymentAmount, setPaymentAmount] = useState(0);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [checkoutProcessing, setCheckoutProcessing] = useState(false); // Separate state for checkout button

    // Stepper state for check-out process
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Booking Details', 'Orders & Charges', 'Bill Summary', 'Payment & Confirm'];

    // Permissions
    const [userRole, setUserRole] = useState('');

    useEffect(() => {
        setUserRole(getUserRole());
        fetchData();
        fetchRooms();
    }, []);

    // Fetch guest orders when selected record changes
    useEffect(() => {
        if (selectedRecord && selectedRecord.id) {
            fetchGuestOrders(selectedRecord.id);
            fetchCheckoutSummary(selectedRecord.id);
        }
    }, [selectedRecord]);

    // Fetch data functions
    const fetchRooms = async () => {
        try {
            const response = await axios.get(APIRoomNames);
            if (response.data.status === 'success') {
                setRooms(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching room names:", error);
            showToast("Error", "Failed to load rooms", "error");
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = { status: 2 };
            const bookingRes = await axios.get(APIBookingList, { params });

            const bookings = Array.isArray(bookingRes.data.data) ? bookingRes.data.data.reverse() : [];

            setPageState(prev => ({
                ...prev,
                pageData: bookings
            }));

            showToast("Success", TEXT_DATA_LOADED, "success");
        } catch (error) {
            let errorMessage = "Failed to load data";
            if (error.response) {
                errorMessage = error.response.data.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            setError(errorMessage);
            showToast("Error", errorMessage, "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchGuestOrders = async (bookingId) => {
        setLoadingOrders(true);
        try {
            const response = await axios.get(APIGuestOrders, {
                params: { booking_id: bookingId }
            });
            
            if (response.data.status === 'success') {
                setGuestOrders(response.data.data);
                
                // Update checkout form with order totals - ensure proper number parsing
                const ordersTotal = Number(response.data.data.totals?.restaurant_orders) || 0;
                const barOrdersTotal = Number(response.data.data.totals?.bar_orders) || 0;
                const roomTotal = Number(selectedRecord?.total_amount) || 0;
                let grandTotal = roomTotal + ordersTotal + barOrdersTotal;
                
                // Round to 2 decimal places
                grandTotal = Math.round(grandTotal * 100) / 100;
                
                setCheckOutForm(prev => ({
                    ...prev,
                    orders_total: ordersTotal,
                    bar_orders_total: barOrdersTotal,
                    grand_total: grandTotal,
                    final_amount: grandTotal
                }));
            }
        } catch (error) {
            console.error('Error fetching guest orders:', error);
            showToast("Error", "Failed to fetch guest orders", "error");
        } finally {
            setLoadingOrders(false);
        }
    };

    const fetchCheckoutSummary = async (bookingId) => {
        try {
            const response = await axios.get(APICheckoutSummary, {
                params: { booking_id: bookingId }
            });
            
            if (response.data.status === 'success') {
                setCheckoutSummary(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching checkout summary:', error);
        }
    };

    // Permission check
    const hasPermission = (permission) => {
        return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
    };

    // Helper functions
    const getCustomerDisplayName = (record) => {
        if (!record) return 'N/A';
        if (record.booking_type === 2) {
            return record.organisation_name || 'N/A';
        }
        return record.customer_name || 'N/A';
    };

    const getCustomerMobile = (record) => {
        if (!record) return 'N/A';
        if (record.booking_type === 2) {
            if (record.organisation_members && record.organisation_members.length > 0) {
                return record.organisation_members[0].phone || 'N/A';
            }
            return 'N/A';
        }
        return record.customer_mobile || 'N/A';
    };

    const getTotalGuests = (record) => {
        if (!record) return 0;
        if (record.booking_type === 2) {
            return record.organisation_members?.length || 0;
        }
        return (record.adults || 1) + (record.children || 0);
    };

    const getBookingTypeLabel = (record) => {
        if (!record) return 'N/A';
        return record.booking_type === 1 ? 'Individual' : 'Organisation';
    };

    const getCustomerEmail = (record) => {
        if (!record) return 'N/A';
        if (record.booking_type === 2) {
            if (record.organisation_members && record.organisation_members.length > 0) {
                return record.organisation_members[0].email || 'N/A';
            }
            return 'N/A';
        }
        return record.customer_email || 'N/A';
    };

    // Fixed calculation functions with proper number handling
    const calculateTotalCharges = () => {
        const roomCharges = Number(selectedRecord?.total_amount) || 0;
        const ordersTotal = checkOutForm.include_orders ? (Number(checkOutForm.orders_total) || 0) : 0;
        const barOrdersTotal = checkOutForm.include_bar_orders ? (Number(checkOutForm.bar_orders_total) || 0) : 0;
        const lateCharges = Number(checkOutForm.late_checkout_charges) || 0;
        const damageCharges = Number(checkOutForm.damage_charges) || 0;
        const otherCharges = Number(checkOutForm.other_charges) || 0;
        
        const total = roomCharges + ordersTotal + barOrdersTotal + lateCharges + damageCharges + otherCharges;
        
        // Round to 2 decimal places to avoid floating point issues
        return Math.round(total * 100) / 100;
    };

    const calculateBalanceDue = () => {
        const total = calculateTotalCharges();
        const advancePaid = Number(selectedRecord?.advance_amount) || 0;
        const balance = total - advancePaid;
        
        // Round to 2 decimal places
        return Math.round(balance * 100) / 100;
    };

    // Handle print receipt
    const handlePrintReceipt = () => {
        if (!selectedRecord) return;
        
        // Prepare receipt data
        const receiptData = {
            booking: selectedRecord,
            charges: {
                roomCharges: Number(selectedRecord?.total_amount) || 0,
                restaurantOrders: checkOutForm.include_orders ? Number(guestOrders.totals?.restaurant_orders || 0) : 0,
                barOrders: checkOutForm.include_bar_orders ? Number(guestOrders.totals?.bar_orders || 0) : 0,
                lateCheckoutCharges: Number(checkOutForm.late_checkout_charges) || 0,
                damageCharges: Number(checkOutForm.damage_charges) || 0,
                otherCharges: Number(checkOutForm.other_charges) || 0,
                subtotal: calculateTotalCharges(),
                comments: checkOutForm.comments
            },
            payment: {
                advancePaid: Number(selectedRecord?.advance_amount) || 0,
                balanceDue: calculateBalanceDue(),
                paymentMethod: checkOutForm.payment_method,
                paymentNotes: checkOutForm.payment_notes,
                amountPaid: paymentAmount > 0 ? paymentAmount : calculateBalanceDue(),
                transactionId: `CHK-${selectedRecord?.booking_no}-${Date.now()}`,
                processedBy: checkOutForm.performed_by
            },
            customerInfo: {
                customerName: getCustomerDisplayName(selectedRecord),
                customerType: getBookingTypeLabel(selectedRecord),
                customerMobile: getCustomerMobile(selectedRecord),
                customerEmail: getCustomerEmail(selectedRecord),
                totalGuests: getTotalGuests(selectedRecord)
            }
        };
        
        setPrintReceiptData(receiptData);
        setIsPrintDialogOpen(true);
    };

    const handleClosePrintDialog = () => {
        setIsPrintDialogOpen(false);
        setPrintReceiptData(null);
    };

    // Filter data
    const filteredData = pageState.pageData.filter(record => {
        const customerDisplay = getCustomerDisplayName(record);
        const matchesSearch = searchTerm === '' ||
            (record.booking_no && record.booking_no.toString().includes(searchTerm)) ||
            (customerDisplay.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesSearch;
    });

    // Pagination handlers
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Dialog handlers
    const handleOpenCheckOut = (record) => {
        setSelectedRecord(record);
        const balanceDue = Number(record.total_amount) - Number(record.advance_amount);

        setCheckOutForm(prev => ({
            ...prev,
            booking_id: record.id,
            comments: '',
            branch_id: getUserDetails("branchId"),
            performed_by: getUserDetails("name") || '',
            final_amount: Number(record.total_amount),
            balance_paid: balanceDue > 0 ? balanceDue : 0,
            additional_charges: 0,
            late_checkout_charges: 0,
            damage_charges: 0,
            other_charges: 0,
            include_orders: true,
            include_bar_orders: true,
            payment_method: 'cash',
            payment_notes: ''
        }));

        // Reset stepper
        setActiveStep(0);
        
        // Reset payment amount
        setPaymentAmount(0);

        const checkOutDate = new Date(record.check_out);
        const now = new Date();
        const isEarlyCheckOut = now < checkOutDate;
        const isLateCheckOut = now > checkOutDate;

        if (isEarlyCheckOut) {
            setEarlyCheckOutModal(true);
        } else if (isLateCheckOut) {
            setLateCheckOutModal(true);
        } else {
            setOpenCheckOut(true);
        }
    };

    const handleCloseCheckOut = () => {
        setOpenCheckOut(false);
        setActiveStep(0);
        setSelectedRecord(null);
        setGuestOrders({ orders: [], bar_orders: [], totals: { restaurant_orders: 0, bar_orders: 0, grand_total: 0 } });
        setCheckoutSummary(null);
        setPaymentAmount(0);
        setCheckoutProcessing(false);
    };

    const handleOpenView = (record) => {
        setSelectedRecord(record);
        setOpenView(true);
    };

    const handleCloseView = () => {
        setOpenView(false);
        setSelectedRecord(null);
    };

    // Form handlers with fixed calculations
    const handleCheckOutFormChange = (e) => {
        const { name, value, checked, type } = e.target;
        
        setCheckOutForm(prev => {
            let updatedForm = { ...prev };
            
            if (type === 'checkbox') {
                updatedForm[name] = checked;
            } else {
                updatedForm[name] = value;
            }
            
            // Recalculate totals with proper number handling
            const roomTotal = Number(selectedRecord?.total_amount) || 0;
            const ordersTotal = updatedForm.include_orders ? (Number(guestOrders.totals?.restaurant_orders) || 0) : 0;
            const barOrdersTotal = updatedForm.include_bar_orders ? (Number(guestOrders.totals?.bar_orders) || 0) : 0;
            const lateCharges = Number(updatedForm.late_checkout_charges) || 0;
            const damageCharges = Number(updatedForm.damage_charges) || 0;
            const otherCharges = Number(updatedForm.other_charges) || 0;
            
            let grandTotal = roomTotal + ordersTotal + barOrdersTotal + lateCharges + damageCharges + otherCharges;
            
            // Round to 2 decimal places
            grandTotal = Math.round(grandTotal * 100) / 100;
            
            const advancePaid = Number(selectedRecord?.advance_amount) || 0;
            let balanceDue = grandTotal - advancePaid;
            balanceDue = Math.round(balanceDue * 100) / 100;
            
            updatedForm.orders_total = ordersTotal;
            updatedForm.bar_orders_total = barOrdersTotal;
            updatedForm.grand_total = grandTotal;
            updatedForm.final_amount = grandTotal;
            updatedForm.balance_paid = balanceDue > 0 ? balanceDue : 0;
            
            return updatedForm;
        });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const applyTodayFilter = () => {
        const today = new Date().toISOString().split('T')[0];
        setFilters({
            ...filters,
            dateFrom: today,
            dateTo: today
        });
        setFilterOptions(false);
    };

    const clearFilters = () => {
        setFilters({
            booking_no: '',
            customer_name: '',
            room_type: '',
            status: '2',
            dateFrom: '',
            dateTo: ''
        });
        setSearchTerm('');
        setFilterOptions(false);
    };

    // Export handlers
    const handleExportClick = (event) => { setExportAnchorEl(event.currentTarget); };
    const handleExportClose = () => { setExportAnchorEl(null); };

    const handleExportExcel = async () => {
        try {
            handleExportClose();
            await exportToExcel(pageState.pageData, { roomsData: rooms }, filters);
            showToast("Success", "Excel file downloaded successfully", "success");
        } catch (error) {
            showToast("Error", error.message, "error");
        }
    };

    const handleExportPDF = async () => {
        try {
            handleExportClose();
            await exportToPDF(pageState.pageData, rooms, filters);
            showToast("Success", "PDF file downloaded successfully", "success");
        } catch (error) {
            showToast("Error", error.message, "error");
        }
    };

    const handleExportCSV = async () => {
        try {
            handleExportClose();
            await exportToCSV(pageState.pageData, { roomsData: rooms });
            showToast("Success", "CSV file downloaded successfully", "success");
        } catch (error) {
            showToast("Error", error.message, "error");
        }
    };

    // Stepper handlers
    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    // Main checkout handler - this is called when user clicks "Complete Check-out"
    const handleCompleteCheckout = async () => {
        // Prevent multiple submissions
        if (checkoutProcessing) return;
        
        const balanceDue = calculateBalanceDue();
        
        // If there's a balance due, show payment dialog first
        if (balanceDue > 0.01) { // Using 0.01 to handle floating point precision
            setPaymentAmount(balanceDue);
            setPaymentDialogOpen(true);
        } else {
            // No payment needed, proceed directly with checkout
            await performCheckOut();
        }
    };

    // Payment confirmation handler
    const handlePaymentConfirm = async () => {
        // Validate payment amount
        const balanceDue = calculateBalanceDue();
        if (paymentAmount < balanceDue - 0.01) { // Allow for small rounding differences
            showToast("Error", `Payment amount cannot be less than balance due of ${formatCurrency(balanceDue)}`, "error");
            return;
        }
        
        setPaymentProcessing(true);
        try {
            await performCheckOut();
            setPaymentDialogOpen(false);
        } catch (error) {
            console.error('Payment processing error:', error);
            showToast("Error", error.response?.data?.message || "Payment processing failed", "error");
        } finally {
            setPaymentProcessing(false);
        }
    };

    // Core checkout API call
    const performCheckOut = async () => {
        // Prevent multiple calls
        if (checkoutProcessing) return;
        
        setCheckoutProcessing(true);
        
        try {
            // Validate booking status
            if (selectedRecord.status !== 2) {
                showToast("Error", "Only checked-in bookings can be checked out", "error");
                setCheckoutProcessing(false);
                return;
            }

            const balanceDue = calculateBalanceDue();
            const finalPaidAmount = paymentAmount > 0 ? paymentAmount : (balanceDue > 0 ? balanceDue : 0);
            
            const checkOutData = {
                booking_id: checkOutForm.booking_id,
                include_orders: checkOutForm.include_orders,
                include_bar_orders: checkOutForm.include_bar_orders,
                payment_method: checkOutForm.payment_method,
                payment_notes: checkOutForm.payment_notes || checkOutForm.comments,
                damage_charges: Number(checkOutForm.damage_charges) || 0,
                late_checkout_charges: Number(checkOutForm.late_checkout_charges) || 0,
                other_charges: Number(checkOutForm.other_charges) || 0,
                comments: checkOutForm.comments,
                performed_by: checkOutForm.performed_by,
                paid_amount: Number(finalPaidAmount) || 0,
                balance_paid: balanceDue
            };

            console.log('Sending checkout data:', checkOutData);

            const response = await axios.post(APIProcessCheckout, checkOutData);

            if (response.data.status === 'success') {
                showToast("Success", "Checkout completed successfully", "success");
                
                // Print receipt after successful checkout
                handlePrintReceipt();
                
                // Close checkout dialog
                handleCloseCheckOut();
                
                // Refresh the bookings list
                await fetchData();
            } else {
                showToast("Error", response.data.message || "Checkout failed", "error");
            }
        } catch (error) {
            console.error('Check-out API error:', error);
            let errorMessage = "Checkout failed";
            if (error.response) {
                errorMessage = error.response.data?.message || error.message;
            } else if (error.code === "ERR_NETWORK") {
                errorMessage = TEXT_CONNECTION_ERROR;
            }
            showToast("Error", errorMessage, "error");
        } finally {
            setCheckoutProcessing(false);
        }
    };

    const handleEarlyCheckOutConfirm = () => {
        setEarlyCheckOutModal(false);
        setOpenCheckOut(true);
    };

    const handleEarlyCheckOutCancel = () => {
        setEarlyCheckOutModal(false);
        setSelectedRecord(null);
    };

    const handleLateCheckOutConfirm = () => {
        setLateCheckOutModal(false);
        setOpenCheckOut(true);
    };

    const handleLateCheckOutCancel = () => {
        setLateCheckOutModal(false);
        setSelectedRecord(null);
    };

    const calculateCheckOutHoursDifference = () => {
        if (!selectedRecord || !selectedRecord.check_out) return 0;
        const checkOutDate = new Date(selectedRecord.check_out);
        const now = new Date();
        const diffMs = now - checkOutDate;
        return Math.ceil(diffMs / (1000 * 60 * 60));
    };

    const calculateEarlyCheckOutHours = () => {
        if (!selectedRecord || !selectedRecord.check_out) return 0;
        const checkOutDate = new Date(selectedRecord.check_out);
        const now = new Date();
        const diffMs = checkOutDate - now;
        const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));
        return diffHours > 0 ? diffHours : 0;
    };

    const calculateLateCheckOutHours = () => {
        const diffHours = calculateCheckOutHoursDifference();
        return diffHours > 0 ? diffHours : 0;
    };

    if (loading) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
        </Box>
    );
    
    if (error) return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <Typography color="error">Error: {error}</Typography>
        </Box>
    );

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Typography sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                fontSize: '1.5rem',
                mb: 3
            }}>
                Check-out Management ({formatNumberWithComma2(pageState.pageData.length || 0)})
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<FilterIcon />}
                            onClick={() => setFilterOptions(true)}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            View Options
                        </Button>

                        {hasPermission(PERMISSIONS.EXPORT) && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportClick}
                                sx={{ textTransform: 'none', borderRadius: '8px' }}
                                size="small"
                            >
                                Export Data
                            </Button>
                        )}

                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<RefreshIcon />}
                            onClick={fetchData}
                            sx={{ textTransform: 'none', borderRadius: '8px' }}
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Paper sx={{
                        width: { xs: '100%', sm: '300px' },
                        p: '2px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        marginLeft: { xs: 0, sm: 'auto' },
                        borderRadius: '8px',
                        background: theme.palette.primary.light
                    }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search Bookings..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <Search sx={{ mr: 1, color: 'white' }} />,
                                sx: {
                                    '& fieldset': { border: 'none' },
                                    fontSize: '0.875rem',
                                    color: 'white',
                                    '& input': {
                                        color: 'white',
                                        '&::placeholder': {
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            opacity: 1
                                        }
                                    }
                                }
                            }}
                            size="small"
                        />
                    </Paper>
                </Box>
            </Paper>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{
                                    backgroundColor: theme.palette.primary.main,
                                    '& th': { color: 'white', fontWeight: 'bold', fontSize: '0.95rem' }
                                }}>
                                    <TableCell>#</TableCell>
                                    <TableCell>Booking #</TableCell>
                                    <TableCell>Customer/Organisation</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Guests</TableCell>
                                    <TableCell>Check In</TableCell>
                                    <TableCell>Check Out</TableCell>
                                    <TableCell>Room Type</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((booking, index) => (
                                    <TableRow key={booking.id} hover>
                                        <TableCell>
                                            <Chip label={page * rowsPerPage + index + 1} color="primary" size="small" />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                            {booking.booking_no || `BK${booking.id.toString().padStart(5, '0')}`}
                                        </TableCell>
                                        <TableCell>
                                            {booking.booking_type === 2 ? (
                                                <>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {booking.organisation_name || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        <People fontSize="small" sx={{ fontSize: 12, mr: 0.5 }} />
                                                        {booking.organisation_members?.length || 0} members
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <Typography variant="body2">
                                                        {booking.customer_name || 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {booking.customer_mobile || ''}
                                                    </Typography>
                                                </>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.booking_type === 1 ? 'Individual' : 'Organisation'}
                                                size="small"
                                                color={booking.booking_type === 1 ? 'default' : 'secondary'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={getTotalGuests(booking)} size="small" color="info" icon={<Group />} />
                                        </TableCell>
                                        <TableCell>{booking.check_in ? formatCreatedAtDate(booking.check_in) : 'N/A'}</TableCell>
                                        <TableCell>{booking.check_out ? formatCreatedAtDate(booking.check_out) : 'N/A'}</TableCell>
                                        <TableCell>{getCategoriesNameById(booking.room_type, rooms)}</TableCell>
                                        <TableCell>
                                            <Chip label={formatCurrency(booking.total_amount || 0)} color="success" size="small" />
                                        </TableCell>
                                        <TableCell>
                                            {booking.status === 2 ? (
                                                <Chip label="Checked In" size="small" color="success" />
                                            ) : (
                                                <Chip label={getStatusById(booking.status)} size="small" color="error" />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton onClick={() => handleOpenView(booking)} color="primary">
                                                <ViewIcon />
                                            </IconButton>
                                            {booking.status === 2 && (
                                                <IconButton onClick={() => handleOpenCheckOut(booking)} color="success" title="Check Out">
                                                    <ExitToApp />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        component="div"
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />

                    {filteredData.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                            <Search sx={{ fontSize: 60, mb: 2 }} />
                            <Typography variant="h6">No bookings found</Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            {/* Check-out Dialog */}
            <Dialog
                open={openCheckOut}
                onClose={handleCloseCheckOut}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle sx={{
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: theme.palette.primary.main,
                    color: 'white'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
                        Check Out {selectedRecord?.booking_type === 2 ? 'Organisation' : 'Guest'}
                    </Typography>
                    <IconButton edge="end" color="inherit" onClick={handleCloseCheckOut}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Box sx={{ px: 3, pt: 2, bgcolor: theme.palette.grey[50] }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                <DialogContent dividers sx={{ maxHeight: '70vh' }}>
                    {/* Step 0: Booking Details */}
                    {activeStep === 0 && (
                        <Card>
                            <CardHeader title="Booking Information" avatar={<Receipt color="primary" />} />
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Booking Number:</Typography>
                                        <Typography variant="h6" color="primary">
                                            {selectedRecord?.booking_no || `BK${selectedRecord?.id?.toString().padStart(5, '0')}`}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Booking Type:</Typography>
                                        <Chip label={getBookingTypeLabel(selectedRecord)} size="small" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {selectedRecord?.booking_type === 2 ? 'Organisation Name:' : 'Customer Name:'}
                                        </Typography>
                                        <Typography variant="h6">{getCustomerDisplayName(selectedRecord)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Contact:</Typography>
                                        <Typography variant="body1">{getCustomerMobile(selectedRecord)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Email:</Typography>
                                        <Typography variant="body1">{getCustomerEmail(selectedRecord)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Total Guests:</Typography>
                                        <Chip label={getTotalGuests(selectedRecord)} size="small" color="info" icon={<Group />} />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Check In:</Typography>
                                        <Typography variant="body1">{formatCreatedAtDate(selectedRecord?.check_in)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Check Out:</Typography>
                                        <Typography variant="body1">{formatCreatedAtDate(selectedRecord?.check_out)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Room:</Typography>
                                        <Typography variant="body1">{getCategoriesNameById(selectedRecord?.room_type, rooms)}</Typography>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 1: Orders & Charges */}
                    {activeStep === 1 && (
                        <Stack spacing={3}>
                            {/* Restaurant Orders */}
                            {guestOrders.orders && guestOrders.orders.length > 0 && (
                                <Card>
                                    <CardHeader 
                                        title="Restaurant Orders" 
                                        avatar={<Receipt color="primary" />}
                                        action={
                                            <FormControlLabel
                                                control={
                                                    <Checkbox 
                                                        checked={checkOutForm.include_orders}
                                                        onChange={handleCheckOutFormChange}
                                                        name="include_orders"
                                                        color="primary"
                                                    />
                                                }
                                                label="Include in Bill"
                                            />
                                        }
                                    />
                                    <CardContent>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                                        <TableCell>Order #</TableCell>
                                                        <TableCell>Items</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                        <TableCell>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {guestOrders.orders.map((order) => (
                                                        <TableRow key={order.id}>
                                                            <TableCell>{order.order_number}</TableCell>
                                                            <TableCell>
                                                                {order.kitchen_order_items ? JSON.parse(order.kitchen_order_items).length : 0}
                                                            </TableCell>
                                                            <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                                                            <TableCell>
                                                                <Chip label={order.order_status} size="small" color={order.order_status === 'pending' ? 'warning' : 'success'} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Bar Orders */}
                            {guestOrders.bar_orders && guestOrders.bar_orders.length > 0 && (
                                <Card>
                                    <CardHeader 
                                        title="Bar Orders" 
                                        avatar={<LocalHotel color="primary" />}
                                        action={
                                            <FormControlLabel
                                                control={
                                                    <Checkbox 
                                                        checked={checkOutForm.include_bar_orders}
                                                        onChange={handleCheckOutFormChange}
                                                        name="include_bar_orders"
                                                        color="primary"
                                                    />
                                                }
                                                label="Include in Bill"
                                            />
                                        }
                                    />
                                    <CardContent>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                                        <TableCell>Order #</TableCell>
                                                        <TableCell>Items</TableCell>
                                                        <TableCell align="right">Total</TableCell>
                                                        <TableCell>Status</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {guestOrders.bar_orders.map((order) => (
                                                        <TableRow key={order.id}>
                                                            <TableCell>{order.order_number}</TableCell>
                                                            <TableCell>
                                                                {order.bar_order_items ? JSON.parse(order.bar_order_items).length : 0}
                                                            </TableCell>
                                                            <TableCell align="right">{formatCurrency(order.total_amount)}</TableCell>
                                                            <TableCell>
                                                                <Chip label={order.order_status} size="small" color={order.order_status === 'pending' ? 'warning' : 'success'} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Additional Charges */}
                            <Card>
                                <CardHeader title="Additional Charges" avatar={<AttachMoney color="warning" />} />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Late Check-out Charges"
                                                type="number"
                                                name="late_checkout_charges"
                                                value={checkOutForm.late_checkout_charges}
                                                onChange={handleCheckOutFormChange}
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Damage Charges"
                                                type="number"
                                                name="damage_charges"
                                                value={checkOutForm.damage_charges}
                                                onChange={handleCheckOutFormChange}
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Other Charges"
                                                type="number"
                                                name="other_charges"
                                                value={checkOutForm.other_charges}
                                                onChange={handleCheckOutFormChange}
                                                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Stack>
                    )}

                    {/* Step 2: Bill Summary with Print Button */}
                    {activeStep === 2 && (
                        <Card>
                            <CardHeader 
                                title="Bill Summary" 
                                avatar={<ReceiptLong color="primary" />}
                                action={
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<Print />}
                                        onClick={handlePrintReceipt}
                                        size="small"
                                    >
                                        Print Bill
                                    </Button>
                                }
                            />
                            <CardContent>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableBody>
                                            <TableRow>
                                                <TableCell><strong>Room Charges</strong></TableCell>
                                                <TableCell align="right">
                                                    <strong>{formatCurrency(Number(selectedRecord?.total_amount) || 0)}</strong>
                                                </TableCell>
                                            </TableRow>
                                            {checkOutForm.include_orders && Number(guestOrders.totals?.restaurant_orders) > 0 && (
                                                <TableRow>
                                                    <TableCell><strong>Restaurant Orders</strong></TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(Number(guestOrders.totals.restaurant_orders) || 0)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {checkOutForm.include_bar_orders && Number(guestOrders.totals?.bar_orders) > 0 && (
                                                <TableRow>
                                                    <TableCell><strong>Bar Orders</strong></TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(Number(guestOrders.totals.bar_orders) || 0)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {Number(checkOutForm.late_checkout_charges) > 0 && (
                                                <TableRow>
                                                    <TableCell><strong>Late Check-out Charges</strong></TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(Number(checkOutForm.late_checkout_charges))}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {Number(checkOutForm.damage_charges) > 0 && (
                                                <TableRow>
                                                    <TableCell><strong>Damage Charges</strong></TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(Number(checkOutForm.damage_charges))}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {Number(checkOutForm.other_charges) > 0 && (
                                                <TableRow>
                                                    <TableCell><strong>Other Charges</strong></TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(Number(checkOutForm.other_charges))}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                                                <TableCell><strong>Subtotal</strong></TableCell>
                                                <TableCell align="right">
                                                    <strong>{formatCurrency(calculateTotalCharges())}</strong>
                                                </TableCell>
                                            </TableRow>
                                            {Number(selectedRecord?.advance_amount) > 0 && (
                                                <TableRow>
                                                    <TableCell><strong>Advance Paid</strong></TableCell>
                                                    <TableCell align="right" sx={{ color: 'success.main' }}>
                                                        - {formatCurrency(Number(selectedRecord?.advance_amount) || 0)}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            <TableRow sx={{ bgcolor: theme.palette.warning.light }}>
                                                <TableCell><strong>Balance Due</strong></TableCell>
                                                <TableCell align="right">
                                                    <strong style={{ 
                                                        color: calculateBalanceDue() > 0 ? '#d32f2f' : '#2e7d32',
                                                        fontSize: '1.1rem'
                                                    }}>
                                                        {formatCurrency(calculateBalanceDue())}
                                                    </strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TextField
                                    fullWidth
                                    label="Payment Method"
                                    select
                                    name="payment_method"
                                    value={checkOutForm.payment_method}
                                    onChange={handleCheckOutFormChange}
                                    sx={{ mt: 2 }}
                                >
                                    <MenuItem value="cash">Cash</MenuItem>
                                    <MenuItem value="mobile_money">Mobile Money</MenuItem>
                                    <MenuItem value="bank">Bank Transfer</MenuItem>
                                    <MenuItem value="card">Credit/Debit Card</MenuItem>
                                </TextField>
                                
                                {/* Display warning if balance due is zero or negative */}
                                {calculateBalanceDue() <= 0 && (
                                    <Alert severity="success" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            <strong>No balance due!</strong> The guest has fully paid or overpaid. 
                                            You can proceed with checkout.
                                        </Typography>
                                    </Alert>
                                )}
                                
                                {calculateBalanceDue() > 0 && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            <strong>Amount to collect:</strong> {formatCurrency(calculateBalanceDue())}
                                        </Typography>
                                    </Alert>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 3: Payment & Confirm with Print Button */}
                    {activeStep === 3 && (
                        <Card>
                            <CardHeader 
                                title="Final Confirmation" 
                                avatar={<CheckCircle color="success" />}
                                action={
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<Print />}
                                        onClick={handlePrintReceipt}
                                        size="small"
                                    >
                                        Print Bill
                                    </Button>
                                }
                            />
                            <CardContent>
                                <TextField
                                    fullWidth
                                    label="Check-out Comments"
                                    name="comments"
                                    value={checkOutForm.comments}
                                    onChange={handleCheckOutFormChange}
                                    multiline
                                    rows={3}
                                    placeholder="Any remarks about the stay, damages, or special notes..."
                                    sx={{ mb: 3 }}
                                />

                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Important:</strong> Once checked out, the booking status cannot be reversed.
                                        Please ensure all payments are settled and room inspection is completed.
                                    </Typography>
                                </Alert>
                            </CardContent>
                        </Card>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button color="error" onClick={handleCloseCheckOut} variant="outlined">
                        Cancel
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
                            Back
                        </Button>
                        {activeStep === steps.length - 1 ? (
                            <Button
                                color="success"
                                variant="contained"
                                onClick={handleCompleteCheckout}
                                startIcon={<Paid />}
                                disabled={checkoutProcessing || paymentProcessing}
                            >
                                {checkoutProcessing || paymentProcessing ? <CircularProgress size={24} /> : 'Complete Check-out'}
                            </Button>
                        ) : (
                            <Button onClick={handleNext} color="primary" variant="contained">
                                Next
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Payment Dialog */}
            <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Payment />
                        <Typography variant="h6">Process Payment</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ p: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                Total balance due: <strong>{formatCurrency(calculateBalanceDue())}</strong>
                            </Typography>
                        </Alert>

                        <TextField
                            fullWidth
                            label="Payment Amount"
                            type="number"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(parseFloat(e.target.value))}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            sx={{ mb: 2 }}
                        />

                        <TextField
                            fullWidth
                            label="Payment Method"
                            select
                            value={checkOutForm.payment_method}
                            onChange={(e) => setCheckOutForm(prev => ({ ...prev, payment_method: e.target.value }))}
                        >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="mobile_money">Mobile Money</MenuItem>
                            <MenuItem value="bank">Bank Transfer</MenuItem>
                            <MenuItem value="card">Credit/Debit Card</MenuItem>
                        </TextField>

                        <TextField
                            fullWidth
                            label="Payment Notes"
                            multiline
                            rows={2}
                            value={checkOutForm.payment_notes}
                            onChange={(e) => setCheckOutForm(prev => ({ ...prev, payment_notes: e.target.value }))}
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPaymentDialogOpen(false)} color="error">
                        Cancel
                    </Button>
                    <Button onClick={handlePaymentConfirm} color="success" variant="contained" disabled={paymentProcessing || checkoutProcessing}>
                        {paymentProcessing ? <CircularProgress size={24} /> : 'Confirm Payment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={openView} onClose={handleCloseView} TransitionComponent={Transition} fullWidth maxWidth="md">
                <DialogTitle sx={{ textAlign: 'center', bgcolor: theme.palette.primary.main, color: 'white' }}>
                    <b>Booking Details</b>
                </DialogTitle>
                <DialogContent>
                    {selectedRecord && (
                        <Box sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Booking Number:</b></Typography>
                                    <Typography variant="body1">{selectedRecord.booking_no}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Status:</b></Typography>
                                    <Chip label={selectedRecord.status === 2 ? "Checked In" : getStatusById(selectedRecord.status)} size="small" color={selectedRecord.status === 2 ? "success" : "primary"} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Customer:</b></Typography>
                                    <Typography variant="body1">{getCustomerDisplayName(selectedRecord)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Contact:</b></Typography>
                                    <Typography variant="body1">{getCustomerMobile(selectedRecord)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Check In:</b></Typography>
                                    <Typography variant="body1">{formatCreatedAtDate(selectedRecord.check_in)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Check Out:</b></Typography>
                                    <Typography variant="body1">{formatCreatedAtDate(selectedRecord.check_out)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Room:</b></Typography>
                                    <Typography variant="body1">{getCategoriesNameById(selectedRecord.room_type, rooms)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Total Amount:</b></Typography>
                                    <Typography variant="body1" fontWeight="bold" color="primary">{formatCurrency(selectedRecord.total_amount)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Advance Paid:</b></Typography>
                                    <Typography variant="body1" color="success">{formatCurrency(selectedRecord.advance_amount)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="subtitle2"><b>Balance Due:</b></Typography>
                                    <Typography variant="body1" color="warning">{formatCurrency(selectedRecord.total_amount - selectedRecord.advance_amount)}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseView} color="error" variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            {/* Early Check-out Confirmation Modal */}
            <Dialog
                open={earlyCheckOutModal}
                onClose={handleEarlyCheckOutCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: theme.palette.info.light,
                    color: theme.palette.info.dark,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Alert severity="info" sx={{ p: 0, backgroundColor: 'transparent' }} />
                    Early Check-out Request
                </DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ py: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                You are attempting to check out {selectedRecord?.booking_type === 2 ? 'an organisation' : 'a guest'} earlier than the scheduled time.
                            </Typography>
                        </Alert>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Schedule Information
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CalendarToday color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Scheduled Check-in" 
                                                    secondary={formatCreatedAtDate(selectedRecord?.check_in)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CalendarToday color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Scheduled Check-out" 
                                                    secondary={formatCreatedAtDate(selectedRecord?.check_out)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <AccessTime color="info" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Current Time" 
                                                    secondary={new Date().toLocaleString()}
                                                />
                                            </ListItem>
                                        </List>

                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                                            <Typography variant="body2" color="info.dark">
                                                <strong>Note:</strong> This is {calculateEarlyCheckOutHours()} hours earlier than scheduled.
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            {selectedRecord?.booking_type === 2 ? 'Organisation Details' : 'Customer Details'}
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {selectedRecord?.booking_type === 2 ? <Business color="primary" /> : <Person color="primary" />}
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={selectedRecord?.booking_type === 2 ? "Organisation Name" : "Customer Name"} 
                                                    secondary={getCustomerDisplayName(selectedRecord)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Phone color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Contact Number" 
                                                    secondary={getCustomerMobile(selectedRecord)}
                                                />
                                            </ListItem>
                                            {selectedRecord?.booking_type === 2 && (
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Group color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Total Members" 
                                                        secondary={selectedRecord?.organisation_members?.length || 0}
                                                    />
                                                </ListItem>
                                            )}
                                            <ListItem>
                                                <ListItemIcon>
                                                    <LocalHotel color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Room Assigned" 
                                                    secondary={getCategoriesNameById(selectedRecord?.room_type, rooms) || 'N/A'}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <AccessTime color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Stay Duration" 
                                                    secondary={`${Math.ceil((new Date(selectedRecord?.check_out) - new Date(selectedRecord?.check_in)) / (1000 * 60 * 60 * 24))} nights`}
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Payment Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Booking Number</Typography>
                                                <Typography variant="body1" fontWeight="bold">
                                                    {selectedRecord?.booking_no}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Payment Status</Typography>
                                                <Chip 
                                                    label={
                                                        (selectedRecord?.advance_amount || 0) >= (selectedRecord?.total_amount || 0) 
                                                        ? "Paid" 
                                                        : "Partial"
                                                    }
                                                    color={
                                                        (selectedRecord?.advance_amount || 0) >= (selectedRecord?.total_amount || 0) 
                                                        ? "success" 
                                                        : "warning"
                                                    }
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Advance Paid</Typography>
                                                <Typography variant="body1" color="success.main">
                                                    {formatCurrency(selectedRecord?.advance_amount || 0)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={3}>
                                                <Typography variant="subtitle2" color="text.secondary">Balance Due</Typography>
                                                <Typography variant="body1" color="warning.main">
                                                    {formatCurrency((selectedRecord?.total_amount || 0) - (selectedRecord?.advance_amount || 0))}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Additional Options
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Early Check-out Reason (Optional)"
                                            multiline
                                            rows={2}
                                            placeholder="Reason for early check-out (e.g., early departure, change in plans...)"
                                            sx={{ mb: 2 }}
                                            onChange={(e) => {
                                                setCheckOutForm(prev => ({
                                                    ...prev,
                                                    comments: `Early check-out: ${e.target.value}. ${prev.comments || ''}`
                                                }));
                                            }}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    color="primary"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setCheckOutForm(prev => ({
                                                                ...prev,
                                                                comments: `[Early check-out - no penalty applied] ${prev.comments || ''}`
                                                            }));
                                                        }
                                                    }}
                                                />
                                            }
                                            label="No penalty for early check-out"
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button onClick={handleEarlyCheckOutCancel} color="error" variant="outlined" startIcon={<CloseIcon />}>
                        Cancel Check-out
                    </Button>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button onClick={handleEarlyCheckOutConfirm} color="info" variant="contained" startIcon={<CheckCircle />}>
                            Proceed with Early Check-out
                        </Button>
                        <Button
                            onClick={() => {
                                showToast("Info", "Please review the payment details in the next step", "info");
                                setEarlyCheckOutModal(false);
                                setOpenCheckOut(true);
                            }}
                            color="primary"
                            variant="outlined"
                            startIcon={<Payment />}
                        >
                            Review Payment Details
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Late Check-out Confirmation Modal */}
            <Dialog
                open={lateCheckOutModal}
                onClose={handleLateCheckOutCancel}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ 
                    backgroundColor: theme.palette.warning.light,
                    color: theme.palette.warning.dark,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Alert severity="warning" sx={{ p: 0, backgroundColor: 'transparent' }} />
                    Late Check-out Detected
                </DialogTitle>

                <DialogContent dividers>
                    <Box sx={{ py: 2 }}>
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {selectedRecord?.booking_type === 2 ? 'Organisation' : 'Guest'} is checking out later than the scheduled time.
                            </Typography>
                        </Alert>

                        <Grid container spacing={3}>
                            {/* Schedule Information */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined" sx={{ mb: 2 }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Schedule Information
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <CalendarToday color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Scheduled Check-out" 
                                                    secondary={formatCreatedAtDate(selectedRecord?.check_out)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <AccessTime color="warning" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Current Time" 
                                                    secondary={new Date().toLocaleString()}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Warning color="warning" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Hours Late" 
                                                    secondary={`${calculateLateCheckOutHours()} hours`}
                                                />
                                            </ListItem>
                                        </List>

                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                                            <Typography variant="body2" color="warning.dark">
                                                <strong>Note:</strong> This check-out is {calculateLateCheckOutHours()} hours late. 
                                                Late check-out charges may apply.
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Customer Information */}
                            <Grid item xs={12} md={6}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            {selectedRecord?.booking_type === 2 ? 'Organisation Details' : 'Customer Details'}
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemIcon>
                                                    {selectedRecord?.booking_type === 2 ? <Business color="primary" /> : <Person color="primary" />}
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={selectedRecord?.booking_type === 2 ? "Organisation Name" : "Customer Name"} 
                                                    secondary={getCustomerDisplayName(selectedRecord)}
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Phone color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Contact Number" 
                                                    secondary={getCustomerMobile(selectedRecord)}
                                                />
                                            </ListItem>
                                            {selectedRecord?.booking_type === 2 && (
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <Group color="primary" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary="Total Members" 
                                                        secondary={selectedRecord?.organisation_members?.length || 0}
                                                    />
                                                </ListItem>
                                            )}
                                            <ListItem>
                                                <ListItemIcon>
                                                    <LocalHotel color="primary" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary="Room Assigned" 
                                                    secondary={getCategoriesNameById(selectedRecord?.room_type, rooms) || 'N/A'}
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Late Check-out Charges */}
                            <Grid item xs={12}>
                                <Card variant="outlined">
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            Late Check-out Charges
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            label="Late Check-out Charges"
                                            type="number"
                                            name="late_checkout_charges"
                                            value={checkOutForm.late_checkout_charges}
                                            onChange={handleCheckOutFormChange}
                                            helperText={`Suggested: ${formatCurrency(calculateLateCheckOutHours() * 10)} (${calculateLateCheckOutHours()} hours × 10/hour)`}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoney />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ mb: 2 }}
                                        />

                                        <TextField
                                            fullWidth
                                            label="Late Check-out Reason (Optional)"
                                            multiline
                                            rows={2}
                                            placeholder="Reason for late check-out..."
                                            sx={{ mb: 2 }}
                                            onChange={(e) => {
                                                setCheckOutForm(prev => ({
                                                    ...prev,
                                                    comments: `Late check-out: ${e.target.value}. ${prev.comments || ''}`
                                                }));
                                            }}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    color="warning"
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            const suggestedCharges = calculateLateCheckOutHours() * 10;
                                                            setCheckOutForm(prev => ({
                                                                ...prev,
                                                                late_checkout_charges: suggestedCharges
                                                            }));
                                                        }
                                                    }}
                                                />
                                            }
                                            label={`Apply suggested late check-out charges (${formatCurrency(calculateLateCheckOutHours() * 10)})`}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={handleLateCheckOutCancel}
                        color="error"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                    >
                        Cancel Check-out
                    </Button>

                    <Button
                        onClick={handleLateCheckOutConfirm}
                        color="warning"
                        variant="contained"
                        startIcon={<CheckCircle />}
                    >
                        Proceed with Late Check-out
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Print Receipt Dialog */}
            <Dialog
                open={isPrintDialogOpen}
                onClose={handleClosePrintDialog}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle sx={{ textAlign: 'center', bgcolor: theme.palette.primary.main, color: 'white' }}>
                    <b>Print Receipt - {selectedRecord?.booking_no}</b>
                </DialogTitle>
                <DialogContent>
                    {printReceiptData && (
                        <PrintCheckoutReceipt
                            checkoutData={printReceiptData}
                            rooms={rooms}
                            onPrintComplete={handleClosePrintDialog}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        color="error"
                        onClick={handleClosePrintDialog}
                        variant="contained"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            window.print();
                        }}
                        color="primary"
                        variant="contained"
                        startIcon={<Print />}
                    >
                        Print Now
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default CheckOut;

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});